"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { apiClient } from "@/lib/api-client";
import { buildSession, getErrorMessage } from "./session-helpers";
import { DEFAULT_ACCOUNT_NUMBER } from "./account-selection-context";
import type { TastytradeSession, TastytradeAccount } from "@/types/tastytrade";

interface AuthContextType {
  session: TastytradeSession | null;
  accounts: TastytradeAccount[];
  selectedAccount: string | null;
  isLoading: boolean;
  error: string | null;
  login: (
    username: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  selectAccount: (accountNumber: string) => void;
  authType: "oauth" | "session" | null;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export const useAuthContext = useAuth;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<TastytradeSession | null>(null);
  const [accounts, setAccounts] = useState<TastytradeAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authType, setAuthType] = useState<"oauth" | "session" | null>(null);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/auth/oauth/refresh", {
        method: "POST",
        credentials: "include",
      });

      return response.ok;
    } catch (error) {
      console.error("OAuth token refresh failed:", error);
      return false;
    }
  }, []);

  const hydrateOAuthSession = useCallback(async (): Promise<boolean> => {
    const validationResponse = await fetch("/api/auth/oauth/validate", {
      method: "GET",
      credentials: "include",
    });

    let validation = (await validationResponse.json().catch(() => ({}))) as {
      valid?: boolean;
      needsRefresh?: boolean;
      expiresAt?: number;
    };

    if (!validationResponse.ok && validation.needsRefresh) {
      const refreshed = await refreshToken();
      if (!refreshed) return false;

      const retryResponse = await fetch("/api/auth/oauth/validate", {
        method: "GET",
        credentials: "include",
      });
      validation = (await retryResponse.json().catch(() => ({}))) as {
        valid?: boolean;
        expiresAt?: number;
      };
      if (!retryResponse.ok || !validation.valid) return false;
    } else if (!validationResponse.ok || !validation.valid) {
      return false;
    }

    const accountsResponse = await fetch("/api/auth/accounts/oauth", {
      method: "GET",
      credentials: "include",
    });

    if (!accountsResponse.ok) {
      throw new Error("Failed to load OAuth accounts");
    }

    const accountsPayload = (await accountsResponse.json()) as {
      accounts?: TastytradeAccount[];
    };
    const accountsData = accountsPayload.accounts || [];
    setAccounts(accountsData);

    const savedAccountNumber = localStorage.getItem("selected_account");
    const savedAccount = savedAccountNumber
      ? accountsData.find(
          (account) => account.account["account-number"] === savedAccountNumber,
        )
      : null;
    const selected =
      savedAccount?.account["account-number"] ||
      accountsData.find(
        (account) =>
          account.account["account-number"] === DEFAULT_ACCOUNT_NUMBER,
      )?.account["account-number"] ||
      accountsData[0]?.account["account-number"] ||
      null;

    setSelectedAccount(selected);
    if (selected) {
      localStorage.setItem("selected_account", selected);
    }

    const expiresAt =
      typeof validation.expiresAt === "number"
        ? new Date(validation.expiresAt).toISOString()
        : undefined;
    setSession(
      buildSession(
        "oauth-access-token-cookie",
        { username: "oauth", "external-id": "oauth", email: "" },
        expiresAt,
      ),
    );
    setAuthType("oauth");
    return true;
  }, [refreshToken]);

  const checkSession = useCallback(async () => {
    console.log("AuthContext: Starting session check...");
    try {
      const oauthReady = await hydrateOAuthSession();
      if (oauthReady) {
        console.log("AuthContext: OAuth session validated successfully");
        return;
      }

      // First, try to validate existing session with timeout
      const validationResponse = await Promise.race<Response>([
        fetch("/api/auth/validate", {
          method: "GET",
          credentials: "include", // Include cookies
        }),
        new Promise<Response>((_, reject) => {
          setTimeout(() => reject(new Error("Session check timeout")), 6000);
        }),
      ]);

      if (!(validationResponse instanceof Response)) {
        throw new Error("Session validation did not return a Response");
      }

      if (validationResponse.ok) {
        const parsed = (await validationResponse.json()) as Record<string, unknown>;
        const sessionToken =
          typeof parsed.sessionToken === "string" ? parsed.sessionToken : null;
        const sessionExpiration =
          typeof parsed.sessionExpiration === "string"
            ? parsed.sessionExpiration
            : undefined;
        const user = parsed.user;

        if (!sessionToken) {
          console.warn(
            "AuthContext: Validation succeeded but no session token returned",
          );
          throw new Error("Missing session token from validation response");
        }
        console.log("AuthContext: Session validated successfully");

        // Create a session object from validation response (token is in httpOnly cookie)
        const sessionData = buildSession(sessionToken, user, sessionExpiration);

        setSession(sessionData);
        setAuthType("session");

        // Fetch accounts with the validated session
        try {
          console.log("AuthContext: Fetching accounts...");
          // Try with a longer timeout; if it fails, attempt one retry before surfacing error
          let accountsData: TastytradeAccount[] | null = null;
          try {
            accountsData = await Promise.race<TastytradeAccount[]>([
              apiClient.fetchAccounts(),
              new Promise<TastytradeAccount[]>((_, reject) =>
                setTimeout(
                  () => reject(new Error("Accounts fetch timeout")),
                  8000,
                ),
              ),
            ]);
          } catch (firstErr) {
            console.warn(
              "AuthContext: Accounts fetch timed out, retrying once...",
              firstErr,
            );
            accountsData = await apiClient.fetchAccounts();
          }
          console.log("AuthContext: Accounts fetched:", accountsData.length);
          setAccounts(accountsData);

          // Restore selected account if saved
          const savedAccountNumber = localStorage.getItem("selected_account");
          if (savedAccountNumber && accountsData.length > 0) {
            const savedAccount = accountsData.find(
              (acc) => acc.account["account-number"] === savedAccountNumber,
            );
            if (savedAccount) {
              setSelectedAccount(savedAccount.account["account-number"]);
            }
          }
        } catch (accountErr) {
          console.error("Failed to fetch accounts:", accountErr);
          setError("Failed to load accounts");
        }
      } else {
        // Session validation failed, try to restore with remember token
        console.log(
          "AuthContext: Session validation failed, trying remember token...",
        );

        localStorage.removeItem("tastytrade_username");
      }
    } catch (error) {
      console.error("Session check failed:", error);
    } finally {
      console.log("AuthContext: Setting isLoading to false");
      setIsLoading(false);
    }
  }, [hydrateOAuthSession]);

  useEffect(() => {
    // Check for existing session on mount
    void checkSession();
  }, [checkSession]);

  const login = async (
    _username: string,
    _password: string,
    _rememberMe: boolean = false,
  ) => {
    const message = "Username/password login is disabled. Use tastytrade OAuth.";
    setError(message);
    throw new Error(message);
  };

  const logout = async () => {
    setIsLoading(true);

    try {
      // Call logout endpoint to clear server-side cookies
      await fetch(authType === "oauth" ? "/api/auth/oauth/logout" : "/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });

      setSession(null);
      setAccounts([]);
      setSelectedAccount(null);
      setAuthType(null);

      // Clear all stored data
      localStorage.removeItem("tastytrade_username");
      localStorage.removeItem("selected_account");

      // Session cleared from cookies by apiClient.logout()
    } catch (error: unknown) {
      const message = getErrorMessage(error, "Logout failed");
      setError(message);
      throw error instanceof Error ? error : new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const selectAccount = (accountNumber: string) => {
    setSelectedAccount(accountNumber);
    localStorage.setItem("selected_account", accountNumber);
  };

  const value: AuthContextType = {
    session,
    accounts,
    selectedAccount,
    isLoading,
    error,
    login,
    logout,
    selectAccount,
    authType,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
